import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type {
  GroupAddress,
  GroupContactInput,
} from '@/admin/hooks/use-property-groups'
import type { AddressValue } from '@/shared/components/ui/address-input'
import { defineField } from '@/shared/lib/forms'
import type { FormFieldMeta } from '@/shared/lib/forms'

/** Composite address field value <-> the group endpoints' flat address. */
export function toGroupAddress(a: unknown): GroupAddress {
  const v = (a ?? {}) as Partial<AddressValue>
  return {
    line1: v.addressLine1?.trim() ?? '',
    line2: v.addressLine2?.trim() ?? '',
    city: v.locality?.trim() ?? '',
    state: v.region?.trim() ?? '',
    zip_code: v.postalCode?.trim() ?? '',
    country: v.countryCode?.trim() ?? '',
  }
}

export function fromGroupAddress(a: GroupAddress | undefined): AddressValue | null {
  if (!a || !a.line1) return null
  return {
    addressLine1: a.line1,
    addressLine2: a.line2,
    locality: a.city,
    region: a.state,
    postalCode: a.zip_code,
    countryCode: a.country,
  }
}

/** The contact fields, shared by the add and edit dialogs. */
export function useContactFields(): FormFieldMeta[] {
  const { t } = useTranslation()
  return useMemo(
    () => [
      defineField({
        code_name: 'first_name',
        display_name: t('groups.contact.firstName', 'First Name'),
        field_type: 'TEXT',
        required: true,
      }),
      defineField({
        code_name: 'last_name',
        display_name: t('groups.contact.lastName', 'Last Name'),
        field_type: 'TEXT',
        required: true,
      }),
      defineField({
        code_name: 'email',
        display_name: t('groups.contact.email', 'Email'),
        field_type: 'EMAIL',
        hint: t('groups.contact.emailOrPhone', 'Email or phone is required.'),
      }),
      defineField({
        code_name: 'phone',
        display_name: t('groups.contact.phone', 'Phone'),
        field_type: 'PHONE',
      }),
    ],
    [t],
  )
}

export function toContactInput(values: Record<string, unknown>): GroupContactInput {
  const s = (k: string) => String(values[k] ?? '').trim()
  return {
    first_name: s('first_name'),
    last_name: s('last_name'),
    email: s('email'),
    phone: s('phone'),
  }
}

